import * as UI from "@iyk/ui"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import * as React from "react"

import { Carousel, HlsVideo, ProtectedImage } from "@iyk/vf-ui"
import { ActivityEvent } from "../../activity/activity.ts"
import { useActivity } from "../../activity/use-activity.tsx"
import { ExclusiveCard } from "./exclusive-card.tsx"
import { useExclusiveSheet } from "./use-exclusive-sheet.ts"

const MAX_DESCRIPTION_HEIGHT_IN_PIXELS = 40

export const ExclusiveGalleryCard = (props: ExclusiveGalleryCard.Props) => {
  const [open, setOpen] = useExclusiveSheet(props.id)
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(0)
  const activity = useActivity()

  const videoRefs = React.useRef<HTMLVideoElement[]>([])

  const [showMore, setShowMore] = React.useState(false)

  const descriptionRef = React.useRef<HTMLParagraphElement>(null)
  const [descriptionHeight, setDescriptionHeight] = React.useState(0)

  const setDescriptionRef = (element: HTMLParagraphElement | null) => {
    if (element) {
      setDescriptionHeight(element.scrollHeight)
      descriptionRef.current = element
    }
  }

  // Video logic

  const setVideoRef = (index: number, isCurrentSlide: boolean) => (element: HTMLVideoElement | null) => {
    if (element) {
      videoRefs.current[index] = element
    }
  }

  // Gallery logic

  const handleSlideChange = (newIndex: number) => {
    const previousVideo = videoRefs.current[currentSlideIndex]
    if (previousVideo && !previousVideo.paused) {
      previousVideo.pause()
    }
    setCurrentSlideIndex(newIndex)
  }
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      videoRefs.current.forEach((video) => video?.pause())
    }
    if (newOpen) {
      activity.track(ActivityEvent.ExclusiveGalleryViewed({ exclusiveId: props.id }))
    }
    setOpen(newOpen)
  }

  const isDescriptionOverflowing = descriptionHeight > MAX_DESCRIPTION_HEIGHT_IN_PIXELS

  return (
    <Sheet.Root open={open} onOpenChange={handleOpenChange}>
      <Sheet.Trigger>
        <ExclusiveCard.Container>
          <ExclusiveCard.CoverImage src={props.coverImage} alt={props.title} />
          <ExclusiveCard.IconButton>
            <UI.Icon.Image />
          </ExclusiveCard.IconButton>
          <ExclusiveCard.Heading>
            <ExclusiveCard.Title>{props.title}</ExclusiveCard.Title>
          </ExclusiveCard.Heading>
        </ExclusiveCard.Container>
      </Sheet.Trigger>
      <Sheet.Content title={props.title} description={props.title}>
        <div className="h-dvh relative flex flex-col">
          <GalleryCarousel.Root onSlideChange={handleSlideChange}>
            <span className="text-sm-title text-gray-12 pt-6 flex flex-col gap-1 items-center justify-start basis-[15%]">
              <span className="max-w-[80%] text-center text-balance">{props.title}</span>
              <GalleryCarousel.Counter />
            </span>
            <GalleryCarousel.Container>
              <GalleryCarousel.Slides data={props.media}>
                {(media, index, isCurrentSlide, isInView) => (
                  <GallerySlide
                    media={media}
                    index={index}
                    isCurrentSlide={isCurrentSlide}
                    isInView={isInView}
                    setVideoRef={setVideoRef}
                  />
                )}
              </GalleryCarousel.Slides>
            </GalleryCarousel.Container>
          </GalleryCarousel.Root>
          <div
            className={UI.cx([
              "inset-0 size-full absolute bg-[linear-gradient(180deg,_rgba(17,17,17,0)_0%,_#111_70%)] pointer-events-none",
              "transition-opacity duration-300 ease-in-out",
              showMore && isDescriptionOverflowing ? "opacity-100 z-10" : "opacity-0"
            ])}
          />
          <div className="grid gap-2 px-4 mb-6 absolute bottom-0 left-0 z-10">
            <span className="text-xs text-gray-12">{props.media[currentSlideIndex].title}</span>
            <p
              ref={setDescriptionRef}
              className={UI.cx([
                "text-xs text-gray-11 transition-all duration-300 ease-in-out overflow-auto hide-scrollbar max-h-[calc(100dvh-10rem)]",
                isDescriptionOverflowing && !showMore && "line-clamp-2",
                showMore && "line-clamp-none"
              ])}
            >
              {props.media[currentSlideIndex].description}
            </p>
            {isDescriptionOverflowing && (
              <button
                type="button"
                onClick={() => setShowMore(!showMore)}
                className={UI.classForLink({ className: "w-fit text-xs" })}
              >
                {showMore ? "Read less" : "Read more"}
              </button>
            )}
          </div>
          <UI.Button
            type="button"
            kind="shaded"
            size="md"
            className="absolute right-4 top-4"
            onClick={() => setOpen(false)}
          >
            <UI.Icon.Cross />
          </UI.Button>
        </div>
      </Sheet.Content>
    </Sheet.Root>
  )
}

const GallerySlide = ({
  media,
  index,
  isCurrentSlide,
  isInView,
  setVideoRef
}: {
  media: ExclusiveGalleryCard.GalleryMedia
  index: number
  isCurrentSlide: boolean
  isInView: boolean
  setVideoRef: (index: number, isCurrentSlide: boolean) => (element: HTMLVideoElement | null) => void
}) => {
  const isHlsVideo = isHlsType.test(media.src)

  if (!isInView) return null

  if (isHlsVideo) {
    return (
      <HlsVideo
        ref={setVideoRef(index, isCurrentSlide)}
        src={media.src}
        autoPlay={isCurrentSlide}
        controls
        muted
        playsInline
        className="size-full"
      />
    )
  }

  return <ProtectedImage src={media.src} alt={media.title} className="size-full object-contain" />
}

const isHlsType = /\.(m3u8)$/i

namespace ExclusiveGalleryCard {
  export type GalleryMedia = {
    id: string
    title: string
    description: string
    src: string
  }

  export type Props = {
    id: string
    title: string
    coverImage: string
    media: Array<GalleryMedia>
  }
}

const Container = ({ children }: GalleryCarousel.Container) => {
  const { scrollSnapList, goToPreviousSlide, goToNextSlide } = Carousel.useCarousel()
  const numSlides = scrollSnapList().length
  const hasAtLeastOneSlide = Boolean(numSlides)
  const hasMultipleSlides = numSlides > 1

  return (
    <div
      data-show={hasAtLeastOneSlide}
      className={UI.cx([
        "relative flex flex-col justify-center aspect-square",
        "transition-opacity duration-300 opacity-0",
        "data-[show=true]:opacity-100"
      ])}
      data-role="carousel-container"
    >
      {hasMultipleSlides && (
        <UI.CircleButton
          type="button"
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10"
          onClick={goToPreviousSlide}
        >
          <UI.Icon.ArrowLeft />
        </UI.CircleButton>
      )}
      {children}
      {hasMultipleSlides && (
        <UI.CircleButton
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10"
          onClick={goToNextSlide}
        >
          <UI.Icon.ArrowRight />
        </UI.CircleButton>
      )}
    </div>
  )
}

const Slides = ({ children, data }: GalleryCarousel.Slides) => {
  const { ref, selectedScrollSnap, slidesInView } = Carousel.useCarousel()

  const selectedLine = selectedScrollSnap()

  return (
    <div ref={ref} className="overflow-hidden aspect-square" data-role="carousel-slides-viewport">
      <div className="flex size-full [touch-action:pan-y_pinch-zoom]" data-role="carousel-slides-container">
        {data.map((image, index) => (
          <Slide key={image.id}>
            {children(image, index, isCurrentSlide(index, selectedLine), slidesInView.includes(index))}
          </Slide>
        ))}
      </div>
    </div>
  )
}

const isCurrentSlide = (index: number, selectedLine: number) => index === selectedLine

const Slide = ({ children }: GalleryCarousel.Slide) => {
  return (
    <section
      className={UI.cx([
        "min-w-0 select-none",
        "[transform:translate3d(0,0,0)]",
        "flex-[0_0_100%]",
        "transition-all duration-300 animate-in",
        "flex items-center justify-center"
      ])}
    >
      {children}
    </section>
  )
}

const Counter = () => {
  const useCarousel = Carousel.useCarousel()

  const lines = useCarousel.scrollSnapList()
  const selectedLine = useCarousel.selectedScrollSnap()

  return (
    <span className="text-xs text-gray-11 px-4">
      {selectedLine + 1} / {lines.length}
    </span>
  )
}

namespace GalleryCarousel {
  export type Container = {
    children: React.ReactNode
  }

  export type Slides = {
    children: (
      media: ExclusiveGalleryCard.GalleryMedia,
      index: number,
      isCurrentSlide: boolean,
      isInView: boolean
    ) => React.ReactNode
    data: Array<ExclusiveGalleryCard.GalleryMedia>
  }

  export type Slide = {
    children: React.ReactNode
  }
}

const GalleryCarousel = {
  Root: Carousel.Root,
  Container: Container,
  Slides: Slides,
  Slide: Slide,
  Counter: Counter
}

// #region Gallery Card Sheet

const SheetRoot = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = () => (
  <SheetPrimitive.Overlay
    className={UI.cx(
      "fixed inset-0 z-50 bg-gray-1",
      "transition ease-in-out",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "data-[state=closed]:duration-500 data-[state=open]:duration-500"
    )}
  />
)
const sheetContentClasses = UI.cx([
  "fixed max-w-[var(--app-max-width)] mx-auto z-50 gap-4 bg-background shadow-lg transition ease-in-out text-foreground",
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:duration-500 data-[state=open]:duration-500",
  "inset-x-0 top-0",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
])

const SheetContent = ({ children, title, description, ...props }: Sheet.ContentProps) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      className={sheetContentClasses}
      onOpenAutoFocus={(e) => e.preventDefault()}
      {...props}
    >
      <div className="sr-only">
        <SheetPrimitive.Title>{title}</SheetPrimitive.Title>
        {description && <SheetPrimitive.Description>{description}</SheetPrimitive.Description>}
      </div>
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
)

namespace Sheet {
  export type ContentProps = {
    title: string
    description?: string
    children: React.ReactNode
  } & SheetPrimitive.DialogContentProps
}

const Sheet = {
  Root: SheetRoot,
  Content: SheetContent,
  Overlay: SheetOverlay,
  Portal: SheetPortal,
  Trigger: SheetTrigger
}

// #endregion
